import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, doc, getDoc, setDoc, updateDoc, deleteDoc, addDoc, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import ConfirmAction from '../../components/common/ConfirmAction';

// ═══════════════════════════════════════════════════════════════
// PROMO CODES
// ═══════════════════════════════════════════════════════════════
export function PromoCodesPanel() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Form state
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState(10);
  const [maxUses, setMaxUses] = useState(100);
  const [expiresAt, setExpiresAt] = useState('');
  const [minFare, setMinFare] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, 'promoCodes'), orderBy('createdAt', 'desc'), limit(100)));
    setPromos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const resetForm = () => {
    setCode(''); setDiscountType('percent'); setDiscountValue(10); setMaxUses(100); setExpiresAt(''); setMinFare(0);
  };

  const handleCreate = async () => {
    if (!code.trim()) { toast.error('Code is required'); return; }
    if (discountValue <= 0) { toast.error('Discount value must be positive'); return; }
    setSubmitting(true);
    try {
      const codeUpper = code.trim().toUpperCase();
      // Check if exists
      const existing = await getDoc(doc(db, 'promoCodes', codeUpper));
      if (existing.exists()) {
        toast.error('A promo with this code already exists');
        setSubmitting(false);
        return;
      }
      await setDoc(doc(db, 'promoCodes', codeUpper), {
        code: codeUpper,
        discountType,
        discountValue: parseFloat(discountValue),
        maxUses: parseInt(maxUses) || null,
        currentUses: 0,
        minFare: parseFloat(minFare) || 0,
        expiresAt: expiresAt ? new Date(expiresAt) : null,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      toast.success(`Promo ${codeUpper} created`);
      resetForm();
      setShowCreate(false);
      reload();
    } catch (err) {
      toast.error(err.message || 'Failed to create');
    }
    setSubmitting(false);
  };

  const handleToggleActive = async (promo) => {
    try {
      await updateDoc(doc(db, 'promoCodes', promo.id), {
        active: !promo.active,
        updatedAt: serverTimestamp(),
      });
      toast.success(`Promo ${promo.active ? 'deactivated' : 'activated'}`);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(db, 'promoCodes', deleteTarget.id));
      toast.success('Promo deleted');
      setDeleteTarget(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-serif m-0">Promo Codes</h1>
        <button onClick={() => setShowCreate(true)}
          className="px-4 py-2 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-semibold">+ New Promo</button>
      </div>

      {loading ? <div className="p-10 text-center text-muted">Loading...</div> : (
        <div className="grid gap-3">
          {promos.map((p) => (
            <div key={p.id} className="p-4 rounded-xl bg-white border border-slate-200">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-bold text-[1.1rem] font-mono">{p.code}</div>
                  <div className="text-[0.85rem] text-body mt-1">
                    {p.discountType === 'percent' ? `${p.discountValue}% off` : `$${p.discountValue} off`}
                    {p.minFare > 0 && ` (min fare $${p.minFare})`}
                  </div>
                  <div className="text-[0.78rem] text-muted mt-1">
                    Used {p.currentUses || 0}/{p.maxUses || '∞'}
                    {p.expiresAt && <> · Expires {new Date(p.expiresAt?.toDate?.() || p.expiresAt).toLocaleDateString()}</>}
                  </div>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold"
                    style={{
                      background: p.active ? 'rgba(45,212,168,0.1)' : 'rgba(148,163,184,0.15)',
                      color: p.active ? '#059669' : '#5a6578',
                    }}>{p.active ? 'ACTIVE' : 'INACTIVE'}</span>
                  <button onClick={() => handleToggleActive(p)}
                    className="px-3 py-[5px] rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer text-[0.78rem] font-semibold">{p.active ? 'Deactivate' : 'Activate'}</button>
                  <button onClick={() => setDeleteTarget(p)}
                    className="px-3 py-[5px] rounded-lg border-[1.5px] border-red-600 bg-white text-red-600 cursor-pointer text-[0.78rem] font-semibold">Delete</button>
                </div>
              </div>
            </div>
          ))}
          {promos.length === 0 && <div className="p-8 text-center text-muted">No promo codes yet</div>}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 flex items-center justify-center z-[1000] p-5" style={{ background: 'rgba(10,22,40,0.6)' }}
          onClick={() => setShowCreate(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-[480px] w-full">
            <h3 className="mt-0 mb-4">Create Promo Code</h3>
            <div className="grid gap-3">
              <FormField label="Code (e.g. WELCOME10)" value={code} onChange={setCode} placeholder="WELCOME10" />
              <div className="grid grid-cols-2 gap-3">
                <FormSelect label="Discount Type" value={discountType} onChange={setDiscountType} options={[
                  { value: 'percent', label: 'Percent off' },
                  { value: 'fixed', label: 'Fixed amount' },
                ]} />
                <FormField label={discountType === 'percent' ? '% off' : 'Amount $'} value={discountValue} onChange={setDiscountValue} type="number" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Max uses" value={maxUses} onChange={setMaxUses} type="number" />
                <FormField label="Min fare $" value={minFare} onChange={setMinFare} type="number" />
              </div>
              <FormField label="Expires (optional)" value={expiresAt} onChange={setExpiresAt} type="date" />
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold">Cancel</button>
              <button onClick={handleCreate} disabled={submitting} className="px-4 py-2 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-semibold">
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmAction
          title={`Delete promo ${deleteTarget.code}?`}
          description="This action cannot be undone. Existing redemptions will not be affected."
          confirmLabel="Delete"
          typedConfirmation="DELETE"
          destructive={true}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SERVICE ZONES
// ═══════════════════════════════════════════════════════════════
export function ZonesPanel() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [active, setActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, 'serviceZones'), orderBy('name', 'asc')));
    setZones(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const handleCreate = async () => {
    const cleanId = zoneId.trim().toLowerCase().replace(/\s+/g, '_');
    if (!cleanId || !name.trim()) { toast.error('Name and ID required'); return; }
    setSubmitting(true);
    try {
      await setDoc(doc(db, 'serviceZones', cleanId), {
        name: name.trim(),
        active,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      toast.success('Zone created');
      setName(''); setZoneId(''); setActive(true);
      setShowCreate(false);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
    setSubmitting(false);
  };

  const toggleZone = async (zone) => {
    try {
      await updateDoc(doc(db, 'serviceZones', zone.id), { active: !zone.active, updatedAt: serverTimestamp() });
      toast.success('Updated');
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-serif m-0">Service Zones</h1>
        <button onClick={() => setShowCreate(true)}
          className="px-4 py-2 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-semibold">+ New Zone</button>
      </div>
      <p className="text-body text-[0.9rem] mb-4">
        Service zones define where drivers can be matched. Drivers select which zones they serve during onboarding.
      </p>

      {loading ? <div className="p-10 text-center text-muted">Loading...</div> : (
        <div className="grid gap-2">
          {zones.map((z) => (
            <div key={z.id} className="p-3.5 rounded-[10px] bg-white border border-slate-200 flex justify-between items-center">
              <div>
                <div className="font-semibold">{z.name}</div>
                <div className="text-[0.78rem] text-muted font-mono">{z.id}</div>
              </div>
              <div className="flex gap-2">
                <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold"
                  style={{
                    background: z.active ? 'rgba(45,212,168,0.1)' : 'rgba(148,163,184,0.15)',
                    color: z.active ? '#059669' : '#5a6578',
                  }}>{z.active ? 'ACTIVE' : 'INACTIVE'}</span>
                <button onClick={() => toggleZone(z)}
                  className="px-3 py-[5px] rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer text-[0.78rem] font-semibold">{z.active ? 'Deactivate' : 'Activate'}</button>
              </div>
            </div>
          ))}
          {zones.length === 0 && <div className="p-8 text-center text-muted">No zones yet</div>}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 flex items-center justify-center z-[1000] p-5" style={{ background: 'rgba(10,22,40,0.6)' }}
          onClick={() => setShowCreate(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-[480px] w-full">
            <h3 className="mt-0 mb-4">New Service Zone</h3>
            <div className="grid gap-3">
              <FormField label="Display Name" value={name} onChange={setName} placeholder="Sydney CBD" />
              <FormField label="Zone ID (lowercase, no spaces)" value={zoneId} onChange={setZoneId} placeholder="sydney_cbd" />
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold">Cancel</button>
              <button onClick={handleCreate} disabled={submitting} className="px-4 py-2 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-semibold">
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TRAINING MODULES
// ═══════════════════════════════════════════════════════════════
export function TrainingModulesPanel() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [required, setRequired] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, 'trainingModules'), orderBy('createdAt', 'desc'), limit(100)));
    setModules(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const handleCreate = async () => {
    if (!title.trim() || !description.trim()) { toast.error('Title and description required'); return; }
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'trainingModules'), {
        title: title.trim(),
        description: description.trim(),
        required,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      toast.success('Module created');
      setTitle(''); setDescription(''); setRequired(true);
      setShowCreate(false);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
    setSubmitting(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-serif m-0">Training Modules</h1>
        <button onClick={() => setShowCreate(true)}
          className="px-4 py-2 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-semibold">+ New Module</button>
      </div>

      {loading ? <div className="p-10 text-center text-muted">Loading...</div> : (
        <div className="grid gap-3">
          {modules.map((m) => (
            <div key={m.id} className="p-4 rounded-xl bg-white border border-slate-200">
              <div className="flex justify-between">
                <div className="flex-1">
                  <div className="font-bold">{m.title}</div>
                  <div className="text-[0.85rem] text-body mt-1">{m.description}</div>
                </div>
                <div className="flex gap-1.5 flex-col items-end">
                  {m.required && <span className="px-2 py-0.5 rounded-full text-[0.7rem] font-bold" style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626' }}>REQUIRED</span>}
                  <span className="px-2 py-0.5 rounded-full text-[0.7rem] font-bold"
                    style={{
                      background: m.active ? 'rgba(45,212,168,0.1)' : 'rgba(148,163,184,0.15)',
                      color: m.active ? '#059669' : '#5a6578',
                    }}>{m.active ? 'ACTIVE' : 'INACTIVE'}</span>
                </div>
              </div>
            </div>
          ))}
          {modules.length === 0 && <div className="p-8 text-center text-muted">No training modules yet</div>}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 flex items-center justify-center z-[1000] p-5" style={{ background: 'rgba(10,22,40,0.6)' }}
          onClick={() => setShowCreate(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-[480px] w-full">
            <h3 className="mt-0 mb-4">New Training Module</h3>
            <div className="grid gap-3">
              <FormField label="Title" value={title} onChange={setTitle} />
              <div>
                <label className="text-[0.85rem] font-semibold text-body mb-1 block">Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                  className="w-full px-3 py-2.5 rounded-[10px] border-[1.5px] border-slate-200 text-[0.95rem] resize-y font-[inherit]" />
              </div>
              <label className="flex items-center gap-2 text-[0.9rem]">
                <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
                Required for all drivers
              </label>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold">Cancel</button>
              <button onClick={handleCreate} disabled={submitting} className="px-4 py-2 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-semibold">
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SYSTEM SETTINGS (super-admin only)
// ═══════════════════════════════════════════════════════════════
export function SystemSettingsPanel() {
  const { userProfile } = useAuth();
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = userProfile?.role === 'superAdmin';

  useEffect(() => {
    const ref = doc(db, 'config', 'system');
    getDoc(ref).then((snap) => {
      const existing = snap.exists() ? snap.data() : {};
      // Merge with defaults so newly-introduced keys appear in the form even if they don't exist in Firestore yet
      setConfig({
        platformCommission: 20,
        cancellationFreeWindow: 24,
        cancellationFee: 10,
        cooldownHours: 24,
        chatTimeWindow: 30,
        maintenanceMode: false,
        // Trip booking window
        minAdvanceBookingMinutes: 60,
        maxAdvanceBookingDays: 30,
        // Rewards & profit-sharing defaults
        profitSharePercent: 75,
        lateCancellationWindowHours: 2,
        lateCancellationPenaltyPoints: 5,
        referralBonusAUD: 5,
        referralBonusPoints: 5,
        ...existing,
      });
    });
  }, []);

  if (!isSuperAdmin) {
    return (
      <div className="p-10 text-center">
        <div className="text-5xl mb-4">🔒</div>
        <h2 className="font-serif">Super Admin Only</h2>
        <p className="text-body">System settings require super-admin access.</p>
      </div>
    );
  }

  if (!config) return <div className="p-10 text-center text-muted">Loading...</div>;

  const updateField = (field, value) => setConfig((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'system'), {
        ...config,
        platformCommission: parseFloat(config.platformCommission) || 0,
        cancellationFreeWindow: parseFloat(config.cancellationFreeWindow) || 0,
        cancellationFee: parseFloat(config.cancellationFee) || 0,
        cooldownHours: parseFloat(config.cooldownHours) || 0,
        chatTimeWindow: parseFloat(config.chatTimeWindow) || 0,
        minAdvanceBookingMinutes: parseInt(config.minAdvanceBookingMinutes) || 0,
        maxAdvanceBookingDays: parseInt(config.maxAdvanceBookingDays) || 0,
        // Rewards & profit-sharing
        profitSharePercent: parseFloat(config.profitSharePercent) || 0,
        lateCancellationWindowHours: parseFloat(config.lateCancellationWindowHours) || 0,
        lateCancellationPenaltyPoints: parseInt(config.lateCancellationPenaltyPoints) || 0,
        referralBonusAUD: parseFloat(config.referralBonusAUD) || 0,
        referralBonusPoints: parseInt(config.referralBonusPoints) || 0,
        updatedAt: serverTimestamp(),
        updatedBy: userProfile.id,
      }, { merge: true });
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.message);
    }
    setSaving(false);
  };

  return (
    <div>
      <h1 className="font-serif mt-0 mb-6">System Settings</h1>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-4">
        <h3 className="mt-0 mb-4">Platform & Commercial</h3>
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Platform Commission (%)" value={config.platformCommission} onChange={(v) => updateField('platformCommission', v)} type="number" />
          <FormField label="Cancellation Free Window (hours)" value={config.cancellationFreeWindow} onChange={(v) => updateField('cancellationFreeWindow', v)} type="number" />
          <FormField label="Cancellation Fee ($)" value={config.cancellationFee} onChange={(v) => updateField('cancellationFee', v)} type="number" />
          <FormField label="Driver-Rider Cooldown (hours)" value={config.cooldownHours} onChange={(v) => updateField('cooldownHours', v)} type="number" />
          <FormField label="Chat Open Window (min before pickup)" value={config.chatTimeWindow} onChange={(v) => updateField('chatTimeWindow', v)} type="number" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-4">
        <h3 className="mt-0 mb-2">Rewards & Profit Sharing</h3>
        <p className="text-[0.85rem] text-body mt-0 mb-4">
          Annual distribution rules. Driver points are awarded per trip based on cancellation rate buckets;
          buckets are configured directly in Firestore (<code>config/system.pointsBuckets</code>).
        </p>
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Profit Share (%)" value={config.profitSharePercent} onChange={(v) => updateField('profitSharePercent', v)} type="number" />
          <FormField label="Late Cancellation Window (hours)" value={config.lateCancellationWindowHours} onChange={(v) => updateField('lateCancellationWindowHours', v)} type="number" />
          <FormField label="Late Cancellation Penalty (points)" value={config.lateCancellationPenaltyPoints} onChange={(v) => updateField('lateCancellationPenaltyPoints', v)} type="number" />
          <FormField label="Referral Bonus ($AUD)" value={config.referralBonusAUD} onChange={(v) => updateField('referralBonusAUD', v)} type="number" />
          <FormField label="Referral Bonus (points)" value={config.referralBonusPoints} onChange={(v) => updateField('referralBonusPoints', v)} type="number" />
        </div>
        <div className="mt-4 p-3 bg-slate-50 rounded-lg text-[0.78rem] text-body">
          <strong className="text-brand-dark">Current points buckets</strong> (cancellation rate → points per completed trip):
          <ul className="mt-1.5 mb-0 ml-[18px] p-0">
            {(config.pointsBuckets || []).map((b, i) => {
              const prev = i > 0 ? (config.pointsBuckets[i - 1].maxRatePercent) : 0;
              const range = i === 0 ? `< ${b.maxRatePercent}%` : (b.maxRatePercent >= 999 ? `≥ ${prev}%` : `${prev}% – < ${b.maxRatePercent}%`);
              return <li key={i} className="mb-0.5">{range} → <strong className="text-brand-dark">{b.points} pt{b.points !== 1 ? 's' : ''}</strong></li>;
            })}
            {(!config.pointsBuckets || config.pointsBuckets.length === 0) && <li className="text-red-600">No buckets configured — run the seed script.</li>}
          </ul>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-4">
        <h3 className="mt-0 mb-3">Trip Booking</h3>
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Min advance booking (minutes before pickup)" value={config.minAdvanceBookingMinutes} onChange={(v) => updateField('minAdvanceBookingMinutes', v)} type="number" />
          <FormField label="Max advance booking (days ahead)" value={config.maxAdvanceBookingDays} onChange={(v) => updateField('maxAdvanceBookingDays', v)} type="number" />
        </div>
        <p className="text-[0.85rem] text-body mt-2 mb-0">Set min to 0 to allow instant on-demand bookings.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-4">
        <h3 className="mt-0 mb-3">Maintenance</h3>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!!config.maintenanceMode} onChange={(e) => updateField('maintenanceMode', e.target.checked)} />
          <span className="font-semibold">Maintenance Mode</span>
        </label>
        <p className="text-[0.85rem] text-body mt-2 mb-0">
          When enabled, new bookings are blocked. Active trips continue normally.
        </p>
      </div>

      <button onClick={handleSave} disabled={saving}
        className="px-5 py-2.5 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold">{saving ? 'Saving...' : 'Save Settings'}</button>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// FARE CONFIG  (config/fareRates + config/peakPricing)
// ═══════════════════════════════════════════════════════════════
export function FareSettingsPanel() {
  const { userProfile } = useAuth();
  const [rates, setRates] = useState(null);
  const [peak, setPeak] = useState(null);
  const [saving, setSaving] = useState(false);
  const isSuperAdmin = userProfile?.role === 'superAdmin';

  useEffect(() => {
    (async () => {
      const rDoc = await getDoc(doc(db, 'config', 'fareRates'));
      const pDoc = await getDoc(doc(db, 'config', 'peakPricing'));
      const defaults = {
        tiers: {
          go: { baseFare: 2.0, perKm: 1.5, perMin: 0.40 },
          comfy: { baseFare: 4.0, perKm: 1.8, perMin: 0.50 },
          plus: { baseFare: 6.0, perKm: 2.8, perMin: 0.70 },
          vip: { baseFare: 10.0, perKm: 3.5, perMin: 0.90 },
          vipPlus: { baseFare: 15.0, perKm: 5.0, perMin: 1.25 },
        },
        levy: 1.2, gstRate: 0.1, minFare: 18.0,
        waitingFreeMinutes: 5, waitingFeePerMin: 1.1,
        stopDwellFreeMinutes: 1, stopDwellRatePerMin: 0.5,
      };
      const ex = rDoc.exists() ? rDoc.data() : {};
      setRates({ ...defaults, ...ex, tiers: { ...defaults.tiers, ...(ex.tiers || {}) } });
      const pe = pDoc.exists() ? pDoc.data() : {};
      setPeak({ windows: Array.isArray(pe.windows) ? pe.windows : [], dateOverrides: Array.isArray(pe.dateOverrides) ? pe.dateOverrides : [] });
    })();
  }, []);

  if (!isSuperAdmin) {
    return (
      <div className="p-10 text-center">
        <div className="text-5xl mb-4">🔒</div>
        <h2 className="font-serif">Super Admin Only</h2>
        <p className="text-body">Fare configuration requires super-admin access.</p>
      </div>
    );
  }
  if (!rates || !peak) return <div className="p-10 text-center text-muted">Loading...</div>;

  const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
  const tierKeys = Object.keys(rates.tiers);
  const TIER_LABELS = { go: 'Go', comfy: 'Comfy', plus: 'Plus', vip: 'VIP', vipPlus: 'VIP+' };
  const DAYS = [['Sun', 0], ['Mon', 1], ['Tue', 2], ['Wed', 3], ['Thu', 4], ['Fri', 5], ['Sat', 6]];

  const setTier = (t, f, v) => setRates((p) => ({ ...p, tiers: { ...p.tiers, [t]: { ...p.tiers[t], [f]: v } } }));
  const setRate = (f, v) => setRates((p) => ({ ...p, [f]: v }));
  const addWindow = () => setPeak((p) => ({ ...p, windows: [...p.windows, { dayOfWeek: [1, 2, 3, 4, 5], startTime: '07:00', endTime: '09:00', surchargePercent: 20 }] }));
  const setWindow = (i, f, v) => setPeak((p) => ({ ...p, windows: p.windows.map((w, idx) => (idx === i ? { ...w, [f]: v } : w)) }));
  const toggleDay = (i, day) => setPeak((p) => ({ ...p, windows: p.windows.map((w, idx) => (idx === i ? { ...w, dayOfWeek: (w.dayOfWeek || []).includes(day) ? w.dayOfWeek.filter((d) => d !== day) : [...(w.dayOfWeek || []), day].sort((a, b) => a - b) } : w)) }));
  const removeWindow = (i) => setPeak((p) => ({ ...p, windows: p.windows.filter((_, idx) => idx !== i) }));
  const addOverride = () => setPeak((p) => ({ ...p, dateOverrides: [...p.dateOverrides, { date: '', surchargePercent: 50 }] }));
  const setOverride = (i, f, v) => setPeak((p) => ({ ...p, dateOverrides: p.dateOverrides.map((o, idx) => (idx === i ? { ...o, [f]: v } : o)) }));
  const removeOverride = (i) => setPeak((p) => ({ ...p, dateOverrides: p.dateOverrides.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const tiersOut = {};
      for (const t of tierKeys) tiersOut[t] = { baseFare: num(rates.tiers[t].baseFare), perKm: num(rates.tiers[t].perKm), perMin: num(rates.tiers[t].perMin) };
      await setDoc(doc(db, 'config', 'fareRates'), {
        tiers: tiersOut,
        levy: num(rates.levy), gstRate: num(rates.gstRate), minFare: num(rates.minFare),
        waitingFreeMinutes: num(rates.waitingFreeMinutes), waitingFeePerMin: num(rates.waitingFeePerMin),
        stopDwellFreeMinutes: num(rates.stopDwellFreeMinutes), stopDwellRatePerMin: num(rates.stopDwellRatePerMin),
        updatedAt: serverTimestamp(), updatedBy: userProfile.id,
      }, { merge: true });
      await setDoc(doc(db, 'config', 'peakPricing'), {
        windows: peak.windows.map((w) => ({ dayOfWeek: (w.dayOfWeek || []).map(Number), startTime: w.startTime, endTime: w.endTime, surchargePercent: num(w.surchargePercent) })),
        dateOverrides: peak.dateOverrides.filter((o) => o.date).map((o) => ({ date: o.date, surchargePercent: num(o.surchargePercent) })),
        updatedAt: serverTimestamp(), updatedBy: userProfile.id,
      }, { merge: true });
      toast.success('Fare config saved');
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  return (
    <div>
      <h1 className="font-serif mt-0 mb-6">Fare Config</h1>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-4">
        <h3 className="mt-0 mb-4">Tier Rates</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[0.9rem]" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr className="text-left text-muted">
                <th className="py-2 pr-3">Tier</th>
                <th className="py-2 pr-3">Base Fare ($)</th>
                <th className="py-2 pr-3">Per Km ($)</th>
                <th className="py-2 pr-3">Per Min ($)</th>
              </tr>
            </thead>
            <tbody>
              {tierKeys.map((t) => (
                <tr key={t} style={{ borderTop: '1px solid #eef2f7' }}>
                  <td className="py-2 pr-3 font-semibold text-brand-dark">{TIER_LABELS[t] || t}</td>
                  <td className="py-2 pr-3"><input type="number" step="0.01" value={rates.tiers[t].baseFare} onChange={(e) => setTier(t, 'baseFare', e.target.value)} className="w-24 px-2 py-1.5 rounded-lg border-[1.5px] border-slate-200" /></td>
                  <td className="py-2 pr-3"><input type="number" step="0.01" value={rates.tiers[t].perKm} onChange={(e) => setTier(t, 'perKm', e.target.value)} className="w-24 px-2 py-1.5 rounded-lg border-[1.5px] border-slate-200" /></td>
                  <td className="py-2 pr-3"><input type="number" step="0.01" value={rates.tiers[t].perMin} onChange={(e) => setTier(t, 'perMin', e.target.value)} className="w-24 px-2 py-1.5 rounded-lg border-[1.5px] border-slate-200" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-4">
        <h3 className="mt-0 mb-4">Charges & Minimums</h3>
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Minimum Fare ($)" value={rates.minFare} onChange={(v) => setRate('minFare', v)} type="number" />
          <FormField label="Passenger Service Levy ($)" value={rates.levy} onChange={(v) => setRate('levy', v)} type="number" />
          <FormField label="GST Rate (e.g. 0.1 = 10%)" value={rates.gstRate} onChange={(v) => setRate('gstRate', v)} type="number" />
          <FormField label="Waiting: free minutes" value={rates.waitingFreeMinutes} onChange={(v) => setRate('waitingFreeMinutes', v)} type="number" />
          <FormField label="Waiting: fee per min ($)" value={rates.waitingFeePerMin} onChange={(v) => setRate('waitingFeePerMin', v)} type="number" />
          <FormField label="Stop dwell: free minutes" value={rates.stopDwellFreeMinutes} onChange={(v) => setRate('stopDwellFreeMinutes', v)} type="number" />
          <FormField label="Stop dwell: rate per min ($)" value={rates.stopDwellRatePerMin} onChange={(v) => setRate('stopDwellRatePerMin', v)} type="number" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="mt-0 mb-0">Surge / Peak Windows</h3>
          <button onClick={addWindow} className="px-3 py-1.5 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-semibold text-[0.85rem]">+ Add window</button>
        </div>
        {peak.windows.length === 0 && <p className="text-muted text-[0.9rem] m-0">No peak windows. Fares are charged at base rate at all times.</p>}
        {peak.windows.map((w, i) => (
          <div key={i} className="p-3 rounded-lg border border-slate-200 mb-3">
            <div className="flex flex-wrap gap-1.5 mb-3">
              {DAYS.map(([lbl, d]) => (
                <button key={d} onClick={() => toggleDay(i, d)}
                  className="px-2.5 py-1 rounded-full text-[0.8rem] font-semibold cursor-pointer border-[1.5px]"
                  style={{ borderColor: (w.dayOfWeek || []).includes(d) ? '#2dd4a8' : '#e2e8f0', background: (w.dayOfWeek || []).includes(d) ? '#2dd4a8' : '#fff', color: (w.dayOfWeek || []).includes(d) ? '#fff' : '#64748b' }}>{lbl}</button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 items-end">
              <div><label className="text-[0.8rem] font-semibold text-body mb-1 block">Start</label><input type="time" value={w.startTime} onChange={(e) => setWindow(i, 'startTime', e.target.value)} className="w-full px-2 py-1.5 rounded-lg border-[1.5px] border-slate-200" /></div>
              <div><label className="text-[0.8rem] font-semibold text-body mb-1 block">End</label><input type="time" value={w.endTime} onChange={(e) => setWindow(i, 'endTime', e.target.value)} className="w-full px-2 py-1.5 rounded-lg border-[1.5px] border-slate-200" /></div>
              <div><label className="text-[0.8rem] font-semibold text-body mb-1 block">Surcharge %</label><input type="number" value={w.surchargePercent} onChange={(e) => setWindow(i, 'surchargePercent', e.target.value)} className="w-full px-2 py-1.5 rounded-lg border-[1.5px] border-slate-200" /></div>
            </div>
            <button onClick={() => removeWindow(i)} className="mt-2 text-[0.82rem] text-red-600 bg-transparent border-0 cursor-pointer p-0">Remove window</button>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="mt-0 mb-0">Date Overrides</h3>
          <button onClick={addOverride} className="px-3 py-1.5 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-semibold text-[0.85rem]">+ Add date</button>
        </div>
        <p className="text-[0.82rem] text-body mt-0 mb-3">A date override replaces all windows for that specific calendar date (e.g. NYE, public holidays).</p>
        {peak.dateOverrides.length === 0 && <p className="text-muted text-[0.9rem] m-0">No date overrides.</p>}
        {peak.dateOverrides.map((o, i) => (
          <div key={i} className="flex gap-3 items-end mb-3">
            <div className="flex-1"><label className="text-[0.8rem] font-semibold text-body mb-1 block">Date</label><input type="date" value={o.date} onChange={(e) => setOverride(i, 'date', e.target.value)} className="w-full px-2 py-1.5 rounded-lg border-[1.5px] border-slate-200" /></div>
            <div className="flex-1"><label className="text-[0.8rem] font-semibold text-body mb-1 block">Surcharge %</label><input type="number" value={o.surchargePercent} onChange={(e) => setOverride(i, 'surchargePercent', e.target.value)} className="w-full px-2 py-1.5 rounded-lg border-[1.5px] border-slate-200" /></div>
            <button onClick={() => removeOverride(i)} className="text-[0.82rem] text-red-600 bg-transparent border-0 cursor-pointer p-0 pb-2">Remove</button>
          </div>
        ))}
      </div>

      <button onClick={handleSave} disabled={saving}
        className="px-5 py-2.5 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold">{saving ? 'Saving...' : 'Save Fare Config'}</button>
    </div>
  );
}


// ─── Form helpers ───
function FormField({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div>
      <label className="text-[0.85rem] font-semibold text-body mb-1 block">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-[10px] border-[1.5px] border-slate-200 text-[0.95rem]" />
    </div>
  );
}

function FormSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-[0.85rem] font-semibold text-body mb-1 block">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-[10px] border-[1.5px] border-slate-200 text-[0.95rem] bg-white">
        {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  );
}